import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { theme } from 'styles/theme';
import {
  toDisplayDate,
  toStorageDate,
  todayStorage,
  compareStorage,
  isDisabledDate,
  buildMonthCells,
  buildRangePresets,
  buildSinglePresets,
  inclusiveDayCount,
} from 'utils/datePickerUtils';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
`;

/** 프리셋 6열·캘린더 7열이 같은 가로폭을 쓰도록 고정 */
const PANEL_WIDTH_PX = 280;

const Panel = styled.div`
  position: fixed;
  z-index: 1201;
  width: ${PANEL_WIDTH_PX}px;
  box-sizing: border-box;
  padding: 8px;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.lg || '0 8px 24px rgba(0,0,0,0.12)'};
  font-size: ${theme.typography.fontSize.sm};
`;

const PresetBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 6px;
  width: 100%;
`;

/** 1·2행 동일 6열 — 열 위치 정렬 */
const PresetRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 4px;
  width: 100%;
`;

const PresetBtn = styled.button`
  margin: 0;
  padding: 3px 2px;
  min-width: 0;
  width: 100%;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.background.light};
  color: ${theme.colors.text.primary};
  font-size: 11px;
  line-height: 1.2;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

/** 프리셋 ↔ 달력 사이 From ~ To */
const DraftRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  width: 100%;
`;

const DraftInput = styled.input`
  flex: 1;
  min-width: 0;
  width: 0;
  height: 26px;
  padding: 0 6px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
  background: ${theme.colors.white};
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const DraftSep = styled.span`
  flex: 0 0 auto;
  color: ${theme.colors.text.secondary};
`;

const Nav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  margin-bottom: 6px;
`;

const NavCenter = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 0;
`;

const TitleBtn = styled.button`
  margin: 0;
  padding: 2px 6px;
  border: 1px solid transparent;
  border-radius: ${theme.borderRadius.sm};
  background: transparent;
  color: ${theme.colors.text.primary};
  font-weight: 700;
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
  }
  &[aria-pressed='true'] {
    color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
  }
`;

const IconBtn = styled.button`
  width: 28px;
  height: 28px;
  margin: 0;
  padding: 0;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.white};
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  flex: 0 0 auto;
  &:hover:not(:disabled) {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const WeekRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 1px;
  margin-bottom: 1px;
  text-align: center;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.tertiary};
  font-weight: 600;
`;

/** 요일 헤더 — 일=빨강, 토=파랑 */
const WeekdayLabel = styled.span`
  color: ${(p) => {
    if (p.$weekday === 0) return theme.colors.red;
    if (p.$weekday === 6) return theme.colors.blue;
    return theme.colors.text.tertiary;
  }};
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 1px;
`;

const YearGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  min-height: 140px;
  align-content: center;
`;

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  min-height: 140px;
  align-content: center;
`;

const PickBtn = styled.button`
  height: 32px;
  margin: 0;
  padding: 0 4px;
  border: 1px solid ${(p) => (p.$selected ? theme.colors.primary : theme.colors.border)};
  border-radius: ${theme.borderRadius.sm};
  background: ${(p) => (p.$selected ? theme.colors.primary : theme.colors.white)};
  color: ${(p) => (p.$selected ? theme.colors.white : theme.colors.text.primary)};
  font-weight: ${(p) => (p.$selected || p.$current ? 700 : 500)};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: ${theme.colors.primary};
    color: ${(p) => (p.$selected ? theme.colors.white : theme.colors.primary)};
  }
`;

const DayBtn = styled.button`
  height: 24px;
  margin: 0;
  padding: 0;
  border: 1px solid transparent;
  border-radius: ${theme.borderRadius.sm};
  background: ${(p) => {
    if (p.$selected) return theme.colors.primary;
    if (p.$inRange) return theme.colors.primaryLight;
    return 'transparent';
  }};
  color: ${(p) => {
    if (p.$disabled) return theme.colors.text.tertiary;
    if (p.$selected) return theme.colors.white;
    if (!p.$inMonth) return theme.colors.text.tertiary;
    // 일요일 빨강 · 토요일 파랑 (기준일·기간 캘린더 공통)
    if (p.$weekday === 0) return theme.colors.red;
    if (p.$weekday === 6) return theme.colors.blue;
    return theme.colors.text.primary;
  }};
  font-weight: ${(p) => (p.$today || p.$selected ? 700 : 500)};
  text-decoration: ${(p) => (p.$today && !p.$selected ? 'underline' : 'none')};
  font-size: ${theme.typography.fontSize.sm};
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.45 : 1)};
  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 1px;
  }
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid ${theme.colors.borderLight || theme.colors.border};
`;

const Hint = styled.span`
  flex: 1;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.tertiary};
  text-align: left;
  min-width: 0;
  white-space: pre-line;
  line-height: 1.35;
`;

const FootActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
`;

const FootBtn = styled.button`
  margin: 0;
  padding: 5px 12px;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$primary ? theme.colors.primary : theme.colors.border)};
  background: ${(p) => (p.$primary ? theme.colors.primary : theme.colors.white)};
  color: ${(p) => (p.$primary ? theme.colors.white : theme.colors.text.primary)};
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const MONTH_LABELS = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

/**
 * 날짜/기간 팝오버
 * - mode=single (기준일): 프리셋·From~To 없음. 일자 클릭/오늘로 즉시 확정
 * - mode=range (기간): 프리셋 + From~To draft → **적용** 시 화면 반영
 */
const DateCalendarPopover = ({
  mode = 'single',
  value = '',
  startValue = '',
  endValue = '',
  min,
  max,
  showPresets = false,
  showTodayShortcut = true,
  presetSet = 'inquiry',
  fiscalYear,
  anchorFrom,
  yearPreset = 'calendar',
  anchorRect,
  onSelect,
  onApplyRange,
  onClose,
}) => {
  const panelRef = useRef(null);

  const initialFrom =
    mode === 'range'
      ? toStorageDate(startValue) || toStorageDate(value) || todayStorage()
      : toStorageDate(value) || todayStorage();
  const initialTo =
    mode === 'range'
      ? toStorageDate(endValue) || toStorageDate(startValue) || toStorageDate(value) || todayStorage()
      : toStorageDate(value) || todayStorage();

  const initDt = (() => {
    const base = initialFrom || todayStorage();
    const y = Number(base.slice(0, 4));
    const m = Number(base.slice(4, 6)) - 1;
    return { y: y || new Date().getFullYear(), m: Number.isFinite(m) ? m : new Date().getMonth() };
  })();

  const [viewY, setViewY] = useState(initDt.y);
  const [viewM, setViewM] = useState(initDt.m);
  /** 'day' | 'year' | 'month' */
  const [panelView, setPanelView] = useState('day');
  const [yearCenter, setYearCenter] = useState(initDt.y);
  const [draftFrom, setDraftFrom] = useState(initialFrom);
  const [draftTo, setDraftTo] = useState(initialTo);
  const [fromText, setFromText] = useState(toDisplayDate(initialFrom));
  const [toText, setToText] = useState(toDisplayDate(initialTo));
  const [pickPhase, setPickPhase] = useState('from');
  const [hoverStor, setHoverStor] = useState('');

  const syncDraft = useCallback((from, to) => {
    const f = toStorageDate(from) || '';
    const t = toStorageDate(to) || f;
    setDraftFrom(f);
    setDraftTo(t);
    setFromText(toDisplayDate(f));
    setToText(toDisplayDate(t));
    // 기간이 한 달을 넘으면 to 월 달력, 아니면 from 월
    const viewStor = f && t && f.slice(0, 6) !== t.slice(0, 6) ? t : f || t;
    if (viewStor) {
      setViewY(Number(viewStor.slice(0, 4)));
      setViewM(Number(viewStor.slice(4, 6)) - 1);
    }
  }, []);

  useEffect(() => {
    const f =
      mode === 'range'
        ? toStorageDate(startValue) || todayStorage()
        : toStorageDate(value) || todayStorage();
    const t =
      mode === 'range'
        ? toStorageDate(endValue) || f
        : toStorageDate(value) || f;
    syncDraft(f, t);
    setPickPhase('from');
    setHoverStor('');
  }, [mode, value, startValue, endValue, syncDraft]);

  const cells = useMemo(() => buildMonthCells(viewY, viewM), [viewY, viewM]);
  const yearOptions = useMemo(
    () => Array.from({ length: 9 }, (_, i) => yearCenter - 4 + i),
    [yearCenter]
  );
  const presets = useMemo(() => {
    if (!showPresets) return [];
    if (mode === 'range') {
      return buildRangePresets(new Date(), { presetSet, fiscalYear, anchorFrom, yearPreset });
    }
    return buildSinglePresets(new Date(), { presetSet, fiscalYear, anchorFrom, yearPreset });
  }, [showPresets, mode, presetSet, fiscalYear, anchorFrom, yearPreset]);
  const presetRows = useMemo(() => {
    const row1 = presets.filter((p) => (p.row || 1) === 1);
    const row2 = presets.filter((p) => p.row === 2);
    return [row1, row2].filter((r) => r.length > 0);
  }, [presets]);

  /** 하단 공간 부족 시 앵커(날짜 필드) 위로 배치 */
  const [pos, setPos] = useState({ top: 0, left: 0 });
  useLayoutEffect(() => {
    if (typeof window === 'undefined' || !anchorRect) return;
    const pad = 4;
    const margin = 8;
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const panelEl = panelRef.current;
    const panelH = panelEl?.offsetHeight || 360;
    const panelW = panelEl?.offsetWidth || PANEL_WIDTH_PX;

    let left = anchorRect.left ?? 0;
    left = Math.min(Math.max(margin, left), Math.max(margin, vw - panelW - margin));

    const belowTop = (anchorRect.bottom || 0) + pad;
    const aboveTop = (anchorRect.top || 0) - pad - panelH;
    const fitsBelow = belowTop + panelH <= vh - margin;
    const fitsAbove = aboveTop >= margin;

    let top;
    if (fitsBelow) {
      top = belowTop;
    } else if (fitsAbove) {
      top = aboveTop;
    } else {
      // 둘 다 부족하면 여유 공간이 큰 쪽에 붙이고 뷰포트 안으로 클램프
      const spaceBelow = vh - (anchorRect.bottom || 0) - pad - margin;
      const spaceAbove = (anchorRect.top || 0) - pad - margin;
      top = spaceAbove > spaceBelow ? Math.max(margin, aboveTop) : Math.min(belowTop, vh - margin - panelH);
      top = Math.max(margin, Math.min(top, vh - margin - Math.min(panelH, vh - 2 * margin)));
    }
    setPos({ top, left });
  }, [anchorRect, panelView, presets.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (panelView !== 'day') {
          setPanelView('day');
          return;
        }
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, panelView]);

  const shiftMonth = (delta) => {
    const d = new Date(viewY, viewM + delta, 1);
    setViewY(d.getFullYear());
    setViewM(d.getMonth());
  };

  const openYearPicker = () => {
    setYearCenter(viewY);
    setPanelView((v) => (v === 'year' ? 'day' : 'year'));
  };

  const openMonthPicker = () => {
    setPanelView((v) => (v === 'month' ? 'day' : 'month'));
  };

  const handleYearNav = (delta) => {
    if (panelView === 'month') return;
    if (panelView === 'year') {
      setYearCenter((y) => y + delta * 9);
      return;
    }
    setViewY((y) => y + delta);
  };

  /** ‹ › 는 월 이동 전용 — 연도 선택 화면에서는 동작하지 않음 */
  const handleMonthNav = (delta) => {
    if (panelView === 'year') return;
    shiftMonth(delta);
  };

  /** 마우스 휠: 연도 선택=연도±, 월 선택=월±, 일자=연도± */
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      const dir = e.deltaY < 0 ? 1 : -1;
      if (panelView === 'year') {
        setYearCenter((y) => y + dir);
      } else if (panelView === 'month') {
        const d = new Date(viewY, viewM + dir, 1);
        setViewY(d.getFullYear());
        setViewM(d.getMonth());
      } else {
        setViewY((y) => y + dir);
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [panelView, viewY, viewM]);

  const previewEnd = useMemo(() => {
    if (!draftFrom) return '';
    if (draftTo) return draftTo;
    if (pickPhase === 'to' && hoverStor) return hoverStor;
    return '';
  }, [draftFrom, draftTo, pickPhase, hoverStor]);

  const rangeBounds = useMemo(() => {
    if (!draftFrom || !previewEnd) return null;
    if (compareStorage(draftFrom, previewEnd) <= 0) {
      return { a: draftFrom, b: previewEnd };
    }
    return { a: previewEnd, b: draftFrom };
  }, [draftFrom, previewEnd]);

  const inRange = useCallback(
    (stor) => {
      if (!rangeBounds) return false;
      return compareStorage(stor, rangeBounds.a) >= 0 && compareStorage(stor, rangeBounds.b) <= 0;
    },
    [rangeBounds]
  );

  /** 캘린더 클릭 → single: 즉시 적용 / range: draft만 */
  const handleDayClick = (stor, disabled) => {
    if (disabled) return;
    if (mode === 'single') {
      syncDraft(stor, stor);
      setPickPhase('from');
      setHoverStor('');
      // 기준일(단일): From~To 없이 일자 클릭으로 바로 확정
      onSelect?.(stor);
      return;
    }
    if (pickPhase === 'from' || !draftFrom) {
      setDraftFrom(stor);
      setDraftTo('');
      setFromText(toDisplayDate(stor));
      setToText('');
      setPickPhase('to');
      setHoverStor('');
      return;
    }
    if (compareStorage(stor, draftFrom) < 0) {
      setDraftFrom(stor);
      setDraftTo('');
      setFromText(toDisplayDate(stor));
      setToText('');
      setPickPhase('to');
      setHoverStor('');
      return;
    }
    setDraftTo(stor);
    setToText(toDisplayDate(stor));
    setPickPhase('from');
    setHoverStor('');
  };

  /** 프리셋 → From/To 필드만 채움 (적용 전 draft). 단일·범위 모두 from/to 유지 */
  const fillPreset = (p) => {
    const from = p.from || p.date;
    const to = p.to || p.date || from;
    syncDraft(from, to);
    setPickPhase('from');
    setHoverStor('');
  };

  const commitDraftInput = (side) => {
    const raw = side === 'from' ? fromText : toText;
    const stor = toStorageDate(raw);
    if (!stor || isDisabledDate(stor, min, max)) {
      if (side === 'from') setFromText(toDisplayDate(draftFrom));
      else setToText(toDisplayDate(draftTo));
      return;
    }
    if (side === 'from') {
      setDraftFrom(stor);
      setFromText(toDisplayDate(stor));
      if (draftTo && compareStorage(stor, draftTo) > 0) {
        setDraftTo(stor);
        setToText(toDisplayDate(stor));
      }
      setViewY(Number(stor.slice(0, 4)));
      setViewM(Number(stor.slice(4, 6)) - 1);
    } else {
      setDraftTo(stor);
      setToText(toDisplayDate(stor));
      if (draftFrom && compareStorage(draftFrom, stor) > 0) {
        setDraftFrom(stor);
        setFromText(toDisplayDate(stor));
      }
    }
  };

  /** 적용: 화면 반영 */
  const handleApply = () => {
    if (!draftFrom && !draftTo) return;
    let from = draftFrom || draftTo;
    let to = draftTo || draftFrom;
    if (compareStorage(from, to) > 0) {
      const tmp = from;
      from = to;
      to = tmp;
    }
    if (mode === 'single') {
      // 기준일·발령일자: to만 반영
      onSelect?.(to);
      return;
    }
    onApplyRange?.({ from, to });
  };

  const fillTodayDraft = () => {
    const t = todayStorage();
    if (isDisabledDate(t, min, max)) return;
    syncDraft(t, t);
    setPickPhase('from');
    // 단일: 「오늘」도 즉시 확정
    if (mode === 'single') onSelect?.(t);
  };

  const dayCount = draftFrom && draftTo ? inclusiveDayCount(draftFrom, draftTo) : 0;
  const canApply = Boolean(draftFrom || draftTo);

  const bodyHint =
    panelView === 'year'
      ? '연도를 선택하세요'
      : panelView === 'month'
        ? '월을 선택하세요'
        : mode === 'single' && (draftTo || draftFrom)
          ? toDisplayDate(draftTo || draftFrom)
          : dayCount > 0
            ? `${toDisplayDate(draftFrom)} ~ ${toDisplayDate(draftTo)}\n(${dayCount}일)`
            : '';

  const showFootToday = showTodayShortcut && presets.length === 0;
  // From~To 입력란은 기간(range) 모드에서만 — 단일 기준일은 캘린더만
  const showDraftInputs = mode === 'range';

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <Overlay onMouseDown={onClose} aria-hidden />
      <Panel
        ref={panelRef}
        style={{ top: pos.top, left: pos.left }}
        role="dialog"
        aria-label={mode === 'range' ? '기간 선택' : '날짜 선택'}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {presetRows.length > 0 && panelView === 'day' && (
          <PresetBlock>
            {presetRows.map((row, idx) => (
              <PresetRow key={`preset-row-${idx}`}>
                {row.map((p) => (
                  <PresetBtn key={p.id} type="button" onClick={() => fillPreset(p)}>
                    {p.label}
                  </PresetBtn>
                ))}
              </PresetRow>
            ))}
          </PresetBlock>
        )}

        {showDraftInputs && panelView === 'day' && (
          <DraftRow>
            <DraftInput
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="YYYY-MM-DD"
              title="시작일"
              aria-label="시작일"
              value={fromText}
              onChange={(e) => setFromText(e.target.value)}
              onBlur={() => commitDraftInput('from')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitDraftInput('from');
                }
              }}
            />
            <DraftSep>~</DraftSep>
            <DraftInput
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="YYYY-MM-DD"
              title="종료일"
              aria-label="종료일"
              value={toText}
              onChange={(e) => setToText(e.target.value)}
              onBlur={() => commitDraftInput('to')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitDraftInput('to');
                }
              }}
            />
          </DraftRow>
        )}

        <Nav>
          <IconBtn
            type="button"
            aria-label="이전 연도"
            title="이전 연도"
            disabled={panelView === 'month'}
            onClick={() => handleYearNav(-1)}
          >
            «
          </IconBtn>
          <IconBtn
            type="button"
            aria-label="이전 달"
            title="이전 달"
            disabled={panelView === 'year'}
            onClick={() => handleMonthNav(-1)}
          >
            ‹
          </IconBtn>
          <NavCenter>
            <TitleBtn
              type="button"
              aria-label="연도 선택"
              aria-pressed={panelView === 'year'}
              title="연도 선택"
              onClick={openYearPicker}
            >
              {viewY}년
            </TitleBtn>
            <TitleBtn
              type="button"
              aria-label="월 선택"
              aria-pressed={panelView === 'month'}
              title="월 선택"
              onClick={openMonthPicker}
            >
              {viewM + 1}월
            </TitleBtn>
          </NavCenter>
          <IconBtn
            type="button"
            aria-label="다음 달"
            title="다음 달"
            disabled={panelView === 'year'}
            onClick={() => handleMonthNav(1)}
          >
            ›
          </IconBtn>
          <IconBtn
            type="button"
            aria-label="다음 연도"
            title="다음 연도"
            disabled={panelView === 'month'}
            onClick={() => handleYearNav(1)}
          >
            »
          </IconBtn>
        </Nav>

        {panelView === 'year' && (
          <YearGrid>
            {yearOptions.map((y) => (
              <PickBtn
                key={y}
                type="button"
                $selected={y === viewY}
                $current={y === yearCenter}
                aria-label={`${y}년`}
                onClick={() => {
                  setViewY(y);
                  setYearCenter(y);
                  setPanelView('day');
                }}
              >
                {y}
              </PickBtn>
            ))}
          </YearGrid>
        )}

        {panelView === 'month' && (
          <MonthGrid>
            {MONTH_LABELS.map((label, idx) => (
              <PickBtn
                key={label}
                type="button"
                $selected={idx === viewM}
                aria-label={`${viewY}년 ${label}`}
                onClick={() => {
                  setViewM(idx);
                  setPanelView('day');
                }}
              >
                {label}
              </PickBtn>
            ))}
          </MonthGrid>
        )}

        {panelView === 'day' && (
          <>
            <WeekRow>
              {WEEKDAYS.map((w, idx) => (
                <WeekdayLabel key={w} $weekday={idx}>
                  {w}
                </WeekdayLabel>
              ))}
            </WeekRow>
            <DayGrid onMouseLeave={() => setHoverStor('')}>
              {cells.map((c, idx) => {
                const weekday = idx % 7; // 0=일 … 6=토
                const disabled = isDisabledDate(c.storage, min, max);
                const selected = draftFrom === c.storage || draftTo === c.storage;
                const ranged = inRange(c.storage) && !selected;
                return (
                  <DayBtn
                    key={c.storage + String(c.inMonth)}
                    type="button"
                    $inMonth={c.inMonth}
                    $today={c.isToday}
                    $selected={selected}
                    $inRange={ranged}
                    $disabled={disabled}
                    $weekday={weekday}
                    disabled={disabled}
                    aria-label={`${toDisplayDate(c.storage)}${c.isToday ? ' 오늘' : ''}${selected ? ' 선택됨' : ''}`}
                    aria-current={c.isToday ? 'date' : undefined}
                    onMouseEnter={() => {
                      if (pickPhase === 'to' && !disabled) setHoverStor(c.storage);
                    }}
                    onClick={() => handleDayClick(c.storage, disabled)}
                  >
                    {c.day}
                  </DayBtn>
                );
              })}
            </DayGrid>
          </>
        )}

        <Foot>
          <Hint>{bodyHint}</Hint>
          <FootActions>
            {showFootToday && (
              <FootBtn type="button" onClick={fillTodayDraft}>
                오늘
              </FootBtn>
            )}
            <FootBtn type="button" $primary disabled={!canApply} onClick={handleApply}>
              적용
            </FootBtn>
            <FootBtn type="button" onClick={onClose}>
              닫기
            </FootBtn>
          </FootActions>
        </Foot>
      </Panel>
    </>,
    document.body
  );
};

export default DateCalendarPopover;
